import { UpdateProductOptions } from '../../store/products/actionsCart';
import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../store/constants';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/notifications/actions';
import { setAmountSingleProductInList } from './setAmountSingleProductInSelectionList';
import { increaseAmountSingleProductInList } from './increaseAmountSingleProductInSelectionList';
import { decreaseAmountSingleProductInList } from './decreaseAmountSingleProductInList';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { invalidateSelectionListSummary } from './useSelectionListSummary';
import { invalidateSelectionListGroupedItems } from './useSelectionListGroupedItems';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { failSelectionlists } from '../../store/orderlist/actions';
import { useState } from 'react';

const buildChangeAmountProductCallback = (
    listId: string,
    product: UpdateProductOptions,
    amount: number,
    command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
): (() => Promise<void>) => {
    const { id: productId, price, subscriptionPeriod } = product;
    const { lendingModel, usageLimit, termLimit } = price ?? {};
    const usageCondition = {
        lendingModel,
        usageLimit,
        termLimit
    };

    const body = {
        usageCondition,
        subscriptionPeriodType: subscriptionPeriod?.type
    };

    return () => {
        switch (command) {
            case ACTION_DECREASE:
                return decreaseAmountSingleProductInList(listId, { body, productId });
            case ACTION_INCREASE:
                return increaseAmountSingleProductInList(listId, { body, productId });
            case ACTION_SET:
                return setAmountSingleProductInList(listId, { body: { ...body, targetAmount: amount }, productId });
        }
    };
};

export const useChangeAmountSingleProductInList = ({ listId }: { listId: string }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setLoading] = useState(false);
    return {
        isLoading,
        changeAmount: async (
            product: UpdateProductOptions,
            amount: number,
            command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
        ) => {
            setLoading(true);
            const apiCallback = buildChangeAmountProductCallback(listId, product, amount, command);
            try {
                await apiCallback();
                dispatch(showNotification(t('notifications.copyAmountChangeListBulk.message')));
                invalidateSelectionListAggregations(listId).then();
                invalidateSelectionListSummary(listId).then();
                invalidateSelectionListGroupedItems(listId).then();
                invalidateSelectionListProducts(listId).then();
            } catch (error) {
                dispatch(failSelectionlists(error));
                invalidateSelectionListProducts(listId).then();
            } finally {
                setLoading(false);
            }
        }
    };
};
